import React from 'react';

import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  MoreHoriz as MoreIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';

import type { DisclaimerInfoFragment } from 'src/api';

type DisclaimerCardProps = {
  disclaimer: DisclaimerInfoFragment;

  onClickEdit: () => void;
  onClickDelete: () => void;
  onClickDetails: () => void;
};

export const DisclaimerCard: React.FC<DisclaimerCardProps> = ({
  disclaimer,
  onClickEdit,
  onClickDelete,
  onClickDetails,
}) => {
  const menuAnchorEl = React.useRef(null);

  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const handleOpen = React.useCallback(() => setIsMenuOpen(true), []);
  const handleClose = React.useCallback(() => {
    setIsMenuOpen(false);
  }, []);

  const titles = React.useMemo(() => disclaimer.titles, [disclaimer.titles]);

  return (
    <Card variant="outlined">
      <CardHeader
        title={disclaimer.name}
        action={
          <IconButton ref={menuAnchorEl} onClick={handleOpen} color="default" size="small">
            <MoreIcon />
          </IconButton>
        }
      />

      <Menu
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        anchorEl={menuAnchorEl.current}
        open={isMenuOpen}
        onClose={handleClose}
        sx={{ mt: 0, marginLeft: '30px' }}
      >
        <MenuItem onClick={onClickDetails}>
          <ListItemIcon>
            <VisibilityIcon />
          </ListItemIcon>
          <ListItemText>View details</ListItemText>
        </MenuItem>
        <MenuItem onClick={onClickEdit}>
          <ListItemIcon>
            <EditIcon />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={onClickDelete} sx={theme => ({ color: theme.palette.error.main })}>
          <ListItemIcon sx={theme => ({ color: theme.palette.error.main })}>
            <DeleteIcon />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      <CardContent>
        <Box display="flex" flexWrap="wrap" gap={1}>
          <Typography color="text.secondary" component="span">
            Titles:
          </Typography>

          {titles.slice(0, 3).map(title => {
            return <Chip key={title} size="small" component="span" label={title} />;
          })}

          {(titles.length || 0) > 3 ? (
            <Chip size="small" component="span" label={`+ ${(titles.length || 0) - 3} titles`} />
          ) : null}
        </Box>

        <Box mt={1}>
          <Typography variant="body1">
            <Typography component="span" color="text.secondary">
              Text:
            </Typography>{' '}
            <Typography component="span">{disclaimer.disclaimer}</Typography>
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};
